import { useEffect, useRef, useState } from "react";

interface UseInViewportOptions {
    /**
     * Отступ вокруг viewport'а, при котором элемент уже считается «видимым» —
     * даёт предзагрузку до фактического попадания в кадр (медиа готово к моменту,
     * как доскроллили; запас под медленный интернет). Формат
     * `IntersectionObserver.rootMargin`.
     */
    rootMargin?: string;
    /**
     * Однажды показанный элемент остаётся видимым навсегда (observer
     * отключается). Для тяжёлого медиа это правильный дефолт: смонтированное
     * `<video>` не размонтируем при выходе из кадра — иначе повторная загрузка
     * первого кадра и мигание при скролле туда-обратно.
     */
    once?: boolean;
}

interface UseInViewportResult<T extends Element> {
    ref: React.RefObject<T | null>;
    isInView: boolean;
}

// Без IntersectionObserver (jsdom в тестах, старые окружения) считаем элемент
// видимым — безопасный дефолт: контент грузится, ничего не прячем.
const ioUnavailable = (): boolean => typeof IntersectionObserver === "undefined";

// Первое число из rootMargin (в px) — насколько расширить зону «уже видим» для
// стартовой прямой проверки геометрии.
const parseMarginPx = (rootMargin: string): number => {
    const n = Number.parseInt(rootMargin, 10);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Отслеживает, попал ли элемент во viewport. Используется для «оконного»
 * монтирования тяжёлого медиа (video) в длинных лентах: узел создаётся только
 * когда тайл близок к кадру, а не все сразу (иначе десятки видео грузятся
 * одновременно). Родительский бокс держит место, поэтому scroll-геометрия не
 * прыгает.
 *
 * Помимо `IntersectionObserver` делает прямую проверку геометрии на следующем
 * кадре после монтирования: observer при открытии ленты не всегда выдаёт первый
 * `intersecting`-callback для элемента, оказавшегося в кадре после программного
 * авто-скролла вниз (нижний ролик висел серым до ручного скролла) — прямая
 * проверка `getBoundingClientRect` это закрывает.
 */
export function useInViewport<T extends Element = HTMLDivElement>({
    rootMargin = "1200px 0px",
    once = true,
}: UseInViewportOptions = {}): UseInViewportResult<T> {
    const ref = useRef<T>(null);
    const [isInView, setIsInView] = useState(ioUnavailable);

    useEffect(() => {
        if (ioUnavailable()) return;
        const el = ref.current;
        if (!el) return;

        // Прямая проверка «уже в кадре» на следующем кадре — после того как
        // стартовый авто-скролл ленты применён.
        const marginPx = parseMarginPx(rootMargin);
        const raf = requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            if (rect.top < vh + marginPx && rect.bottom > -marginPx) setIsInView(true);
        });

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry) return;
                if (entry.isIntersecting) {
                    setIsInView(true);
                    if (once) observer.disconnect();
                } else if (!once) {
                    setIsInView(false);
                }
            },
            { rootMargin },
        );
        observer.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            observer.disconnect();
        };
    }, [rootMargin, once]);

    return { ref, isInView };
}
